import axios from "./axiosInstance";
import config from "./config";
import { captureException } from "../utils/sentry";

const BASE_URL = config.BASE_URL;

// GET
// `params` are applied SERVER-side: { status, post_status, page, limit }.
// post_status is sent as the string "null" to mean IS NULL, since a query
// string cannot carry a real null. Without params the server defaults to the
// first page of 25 — it no longer returns every invoice, because enriching
// all of them was ~100k queries on a club with 24,946.
export const getInvoices = (token, params = {}) => {
  return axios
    .get(`${BASE_URL}/invoices`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
      params,
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

export const getPendingInvoices = (token) => {
  return axios
    .get(`${BASE_URL}/invoices/pending/invoices`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// get Pending Invoices By Member 
export const getPendingInvoicesByMember = (token, membershipId) => {
  return axios
    .get(`${BASE_URL}/invoices/pending/invoices/${membershipId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
export const getInvoicesByMember = (token, id) => {
  return axios
    .get(`${BASE_URL}/invoices/member/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// GET
export const getInvoiceById = (token, invoiceId) => {
  return axios
    .get(`${BASE_URL}/invoices/${invoiceId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err;
    });
};

// GET
export const getFutureInvoicesByMember = (token, membershipId) => {
  return axios
    .get(`${BASE_URL}/invoices/future/${membershipId}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      return err?.response;
    });
};

// GET
export const getDraftInvoiceForMembership = (membershipId, month, token) => {
  return axios
    .get(
      `${BASE_URL}/invoices/draft?membershipId=${membershipId}&month=${month}`,
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// POST
export const createInvoice = (newForm, token) => {
  return axios
    .post(`${BASE_URL}/invoices`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    // INTENTIONAL: best-effort bookkeeping that runs AFTER money has already
    // moved. See the comment at ManageUnpaidInvoice.tsx:259 — a rejection here
    // would surface as a payment failure on a payment that SUCCEEDED. Leave until
    // the callers return a typed result instead of relying on undefined.
    // eslint-disable-next-line no-restricted-syntax
    .catch((err) => { captureException(err); });
};

// PUT
export const updateInvoice = (newForm) => {
  return axios
    .put(`${BASE_URL}/invoices`, newForm, {
      headers: {
        "Content-Type": "application/json",
      },
    })
    .then((res) => {
      if (res) return res;
    })
    // INTENTIONAL: same post-charge bookkeeping contract as createInvoice above.
    // eslint-disable-next-line no-restricted-syntax
    .catch((err) => { captureException(err); });
};

// PUT
export const updateBillingHistoryInvoice = (newForm, token) => {
  return axios
    .put(`${BASE_URL}/invoices/billing-history`, newForm, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// PUT
export const publishDraftInvoice = (invoiceId, token, form) => {
  return axios
    .put(`${BASE_URL}/invoices/publish/${invoiceId}`, form, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// DEL
export const deleteInvoice = (token, id) => {
  return axios
    .delete(`${BASE_URL}/invoices/${id}`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// PUT - Reorder billing history (admin only)
export const reorderBillingHistory = (token, membershipId, invoiceIds) => {
  return axios
    .put(
      `${BASE_URL}/invoices/billing-history/reorder`,
      { membershipId, invoiceIds },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {

      throw err;
    });
};

// GET - the people on an account (names only), for the Responsible / Paid by
// columns and pickers on shared accounts.
export const getAccountPeople = (token, membershipId) => {
  return axios
    .get(`${BASE_URL}/invoices/member/${membershipId}/people`, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${token}`,
      },
    })
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};

// PUT - staff name (or clear, with null) the person responsible for one
// invoice. A label only: it does not change the amount, the balance or which
// card autopay charges.
export const setInvoiceResponsible = (token, invoiceId, membershipUserId) => {
  return axios
    .put(
      `${BASE_URL}/invoices/${invoiceId}/responsible`,
      { membership_user_id: membershipUserId || null },
      {
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
      }
    )
    .then((res) => {
      if (res) return res?.data;
    })
    .catch((err) => {
      captureException(err);
      throw err;
    });
};
